import React, { useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { AssessmentNav } from '../../ui/AssessmentNav';
import { AssessmentBackground } from '../../ui/AssessmentBackground';
import { AssessmentQuestionBackground } from '../../ui/AssessmentQuestionBackground';
import { AssessmentEyebrow } from '../../ui/AssessmentEyebrow';
import { Footer } from '../../ui/Footer/Footer';
import { FadeRise, type FadeRiseStep } from '../../ui/FadeRise';
import styles from './AssessmentStep.module.css';

/*
 * AssessmentStep — generic assessment-flow screen template.
 *
 * Renders chrome (Navbar + Background + Footer) around a structured content
 * well: optional pre-title icon, optional eyebrow, required h1 title,
 * optional body paragraph, flexible main slot (children), action row.
 *
 * Services every screen in the Business Impact Inventory flow — Intro,
 * Rating Scale, How to Respond, Section Divider (Org/Functional Impact),
 * Completion — and any future screen following the same shape. Per-screen
 * variability lives in the props + the children slot.
 *
 *   <AssessmentStep
 *     brand="mayo"
 *     assessmentName="Business Impact Inventory"
 *     percent={30}
 *     eyebrow="Instructions"
 *     title="How to respond"
 *     body="The inventory works best when…"
 *     actions={
 *       <>
 *         <Button variant="secondary">Back</Button>
 *         <Button variant="primary" tone="bright" endIcon={<ButtonArrow />}>Begin</Button>
 *       </>
 *     }
 *   >
 *     {/* Optional middle content — stats, rating scale, sub-sections, etc. *\/}
 *   </AssessmentStep>
 */

export interface AssessmentStepProps {
  /** Forwarded to Navbar's brand prop. */
  brand: 'mayo' | 'hs' | string;
  /** Forwarded to Navbar's assessmentName. */
  assessmentName: ReactNode;
  /** Forwarded to Navbar's percent (0–100). */
  percent: number;
  /** The h1 text. Required. */
  title: string;
  /** Paragraph below the title. */
  body?: ReactNode;
  /** Section label above the title. When passed a string, internally composed
   *  as AssessmentEyebrow. When passed a ReactNode (e.g., a custom
   *  AssessmentQuestionEyebrow for in-flow question screens), rendered as-is. */
  eyebrow?: string | ReactNode;
  /** 80×80 icon slot above the eyebrow. */
  preTitleIcon?: ReactNode;
  /** Action row at the bottom — typically Back link + primary CTA. */
  actions?: ReactNode;
  /** Optional content below the action row (e.g. a validation ErrorMessage).
   *  Sits in the main block, so it picks up the 40px gap below the actions. */
  belowActions?: ReactNode;
  /** Variable middle content between body and actions (the `main` slot). */
  children?: ReactNode;
  /** Forwarded to the root `<div>`. */
  className?: string;
  /** Content-well layout treatment.
   *  - `'hero'` (default) — left-anchored 960px column with the large hero
   *    typography ramp; used by Intro / Section Divider / How to Respond /
   *    Completion variants.
   *  - `'question'` — centered 1000px column with a smaller, center-aligned
   *    typography ramp (h1 + body both at 28px, weights 500/350) and a
   *    centered action row; used by in-flow question screens. Mirrors the
   *    source `Immersive Agility Flow` COMPONENT_SET's `type=question` axis.
   *  - `'sub-section'` — the hero layout (left-anchored 960px column,
   *    left-aligned actions) with a distinct headline treatment: smaller +
   *    lighter than the hero h1, set in Avenir LT Pro, in bright blue
   *    (48/350 desktop, 28/350 mobile). Used by the sub-section divider
   *    screens. Eyebrow + body render exactly as the hero variant. */
  template?: 'hero' | 'question' | 'sub-section';
  /** Render only the content well (skip Navbar / Background / Footer chrome).
   *  Used by the question flow, which renders chrome once and overlaps two
   *  chrome-less steps during a transition. Default `true` (full screen). */
  chrome?: boolean;
  /** Question-transition phase. When set (question template only), the header
   *  block animates per-element instead of statically rendering: the text group
   *  (eyebrow + title + body) on the Y axis, the cards on the X axis, the
   *  actions on the opposite Y axis. `rest` = settled, no animation. */
  phase?: 'rest' | 'enter' | 'exit';
  /** Navigation direction for the transition. 1 = forward (next), -1 = back.
   *  Only the cards carry the directional cue; text + buttons are direction-
   *  independent (per the source prototype). Default 1. */
  direction?: 1 | -1;
  /**
   * Colour treatment. `dark` (default) is the light-ink-on-dark-surface flow;
   * `light` flips the heading / body / sub-heading text to their light values
   * and forwards `theme` to the eyebrow. The surrounding surface (background,
   * nav, footer) is the host prototype's responsibility. A per-instance variant.
   */
  theme?: 'light' | 'dark';
}

export const AssessmentStep: React.FC<AssessmentStepProps> = ({
  brand,
  assessmentName,
  percent,
  title,
  body,
  eyebrow,
  preTitleIcon,
  actions,
  belowActions,
  children,
  className,
  template = 'hero',
  chrome = true,
  phase,
  direction = 1,
  theme = 'dark',
}) => {
  const rootClass = [styles.root, theme === 'light' ? styles.light : '', className]
    .filter(Boolean)
    .join(' ');

  // Question-transition mode: active when a `phase` is supplied. The text group
  // and buttons pin to the 'fwd' (up-axis) keyframes regardless of direction —
  // only the cards flip fwd/bwd — so back-navigation doesn't feel inverted
  // (matches the source prototype).
  const transition = phase !== undefined;
  const dirSuffix = direction > 0 ? 'fwd' : 'bwd';
  const textClass = !transition || phase === 'rest' ? '' : `hd-q-text-${phase}-fwd`;
  const cardsClass = !transition || phase === 'rest' ? '' : `hd-q-cards-${phase}-${dirSuffix}`;
  const buttonsClass = !transition || phase === 'rest' ? '' : `hd-q-buttons-${phase}`;
  const cx = (...names: (string | undefined)[]) => names.filter(Boolean).join(' ');

  // Content-aware vertical compaction (question template, full-chrome only).
  // Measures how far the QUESTION CONTENT overflows the viewport AT FULL SPACING
  // — sticky nav + the content column ONLY. The footer and its gap are
  // intentionally excluded, so as the window shrinks they scroll off FIRST while
  // the content keeps full spacing; the content only squishes (`--compact-t`
  // 0 → 1, tightening each gap down to a 12px floor) once the content itself
  // can't fit. Zero overflow → t=0 → full spacing. Always measured at t=0 so the
  // decision can't oscillate.
  const rootRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (!chrome || template !== 'question') return;
    const root = rootRef.current;
    if (!root || typeof window === 'undefined') return;
    // Total height (px) recovered when t goes 0 → 1 across all tightened gaps:
    // column padding-block 24 + 3 header gaps 12 + mainBlock margin 24 + gap 28.
    const BUDGET = 88;
    const apply = () => {
      root.style.setProperty('--compact-t', '0');
      const nav = root.querySelector('header');
      const column = root.querySelector('main')?.firstElementChild;
      const navH = nav instanceof HTMLElement ? nav.offsetHeight : 0;
      const colH = column instanceof HTMLElement ? column.offsetHeight : 0;
      const overflow = navH + colH - window.innerHeight;
      const t = Math.min(Math.max(overflow / BUDGET, 0), 1);
      root.style.setProperty('--compact-t', String(t));
    };
    apply();
    window.addEventListener('resize', apply);

    // Avenir loads async (self-hosted webfont, font-display: swap) and changes the content height — re-measure.
    if (typeof document !== 'undefined' && document.fonts?.ready) {
      document.fonts.ready.then(apply).catch(() => {});
    }
    return () => window.removeEventListener('resize', apply);
  }, [chrome, template, title]);

  // Frosted nav: on once content has scrolled UNDER the sticky nav, off at the
  // top. Watches whichever element actually scrolls the page beneath the nav —
  // the nearest scrollable ancestor of the root, or the window when the document
  // itself scrolls. Full-chrome only (the chrome-less `contentOnly` variant has
  // no nav). The `scrolled` flag drives AssessmentNav's glass surface.
  const [navScrolled, setNavScrolled] = useState(false);
  useLayoutEffect(() => {
    if (!chrome) return;
    const root = rootRef.current;
    if (!root || typeof window === 'undefined') return;
    let scroller: HTMLElement | Window = window;
    for (let p = root.parentElement; p; p = p.parentElement) {
      const oy = getComputedStyle(p).overflowY;
      if (oy === 'auto' || oy === 'scroll') {
        scroller = p;
        break;
      }
    }
    const read = () =>
      scroller === window ? window.scrollY : (scroller as HTMLElement).scrollTop;
    const onScroll = () => setNavScrolled(read() > 2);
    onScroll();
    scroller.addEventListener('scroll', onScroll, { passive: true });
    return () => scroller.removeEventListener('scroll', onScroll);
  }, [chrome]);

  // A11y: move keyboard focus to the step heading when the step changes, so
  // keyboard + screen-reader users land on (and hear) the new screen instead of
  // being dropped to <body>. Skips the outgoing transition copy (phase 'exit')
  // so it never steals focus mid-slide. preventScroll — the shell owns scroll.
  useLayoutEffect(() => {
    if (phase === 'exit' || typeof document === 'undefined') return;
    const h1 = rootRef.current?.querySelector('h1');
    if (!(h1 instanceof HTMLElement)) return;
    h1.tabIndex = -1;
    h1.focus({ preventScroll: true });
  }, [title, phase]);

  // Header-block entrance. The `hero` / `sub-section` templates rise + fade in
  // sequence (FadeRise). The `question` template does NOT rise — in-flow
  // question screens own a per-element slide transition (handled by the
  // question flow), so applying the screen-rise here would double up. Steps are
  // derived from which optional blocks are present, so the first VISIBLE block
  // always leads the cascade (d1).
  const rise = template !== 'question';
  const order = [
    preTitleIcon ? 'icon' : null,
    eyebrow ? 'eyebrow' : null,
    'title',
    body ? 'body' : null,
    children || actions ? 'main' : null,
  ].filter(Boolean) as string[];
  const stepOf = (key: string) => (order.indexOf(key) + 1) as FadeRiseStep;
  const block = (
    key: string,
    className: string | undefined,
    content: ReactNode,
    as: React.ElementType = 'div',
  ) =>
    rise ? (
      <FadeRise as={as} step={stepOf(key)} className={className}>
        {content}
      </FadeRise>
    ) : (
      React.createElement(as, { className }, content)
    );

  const eyebrowContent =
    typeof eyebrow === 'string' ? <AssessmentEyebrow label={eyebrow} theme={theme} /> : eyebrow;

  return (
    <div
      ref={rootRef}
      className={cx(rootClass, chrome ? undefined : styles.contentOnly)}
      data-template={template}
    >
      {chrome && (
        <a href="#main-content" className={styles.skipLink}>
          Skip to main content
        </a>
      )}

      {chrome &&
        (template === 'question' ? (
          <AssessmentQuestionBackground />
        ) : (
          <AssessmentBackground />
        ))}

      {chrome && (
        <AssessmentNav
          brand={brand}
          assessmentName={assessmentName}
          percent={percent}
          scrolled={navScrolled}
        />
      )}

      <main id={chrome ? 'main-content' : undefined} className={styles.main}>
        <div className={styles.column}>
          {/* Header text block — eyebrow / h1 / body / main slot stack with
           *  uniform 24px gap (matches Figma's "header text" container). */}
          <div className={styles.headerBlock}>
            {transition ? (
              /* Question-transition mode: eyebrow + title + body move together
               *  as one text group (Y axis); cards + actions animate on their
               *  own axes. textGroup re-uses the 24px header gap so grouping is
               *  visually identical to the static stack. */
              <>
                <div className={cx(styles.textGroup, textClass)}>
                  {preTitleIcon && (
                    <div className={styles.preTitleIcon}>{preTitleIcon}</div>
                  )}
                  {eyebrow && <div className={styles.eyebrow}>{eyebrowContent}</div>}
                  <h1 className={styles.title}>{title}</h1>
                  {body && <div className={styles.body}>{body}</div>}
                </div>
                {(children || actions || belowActions) && (
                  <div className={styles.mainBlock}>
                    {children && (
                      <div className={cx(styles.mainSlot, cardsClass)}>{children}</div>
                    )}
                    {actions && (
                      <div className={cx(styles.actions, buttonsClass)}>{actions}</div>
                    )}
                    {belowActions &&
                      <div className={buttonsClass}>
                        {belowActions}
                      </div>
                    }
                  </div>
                )}
              </>
            ) : (
              <>
                {preTitleIcon && block('icon', styles.preTitleIcon, preTitleIcon)}
                {eyebrow && block('eyebrow', styles.eyebrow, eyebrowContent)}
                {block('title', styles.title, title, 'h1')}
                {body && block('body', styles.body, body)}
                {(children || actions || belowActions) &&
                  /* Main slot — stats + CTAs stack with 40px gap (matches Figma's
                   *  "main" container). */
                  block(
                    'main',
                    styles.mainBlock,
                    <>
                      {children && <div className={styles.mainSlot}>{children}</div>}
                      {actions && <div className={styles.actions}>{actions}</div>}
                      {belowActions}
                    </>,
                  )}
              </>
            )}
          </div>
        </div>
      </main>

      {chrome && <Footer variant="assessment" />}
    </div>
  );
};

AssessmentStep.displayName = 'AssessmentStep';
